import { useState, useMemo } from 'react';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { s, Toast, ExcelIcon, toGridSortField, toODataSortField } from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { useC360List, C360Table, ColumnChooser } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import './securityScreens.css';

// G-L-03 — Giriş / çıkış günlüğü. SALT OKUNUR: bu ekranda kayıt eklenmez, düzenlenmez, silinmez.
// Saklama süresi buradan değil, Zamanlanmış Görevler › Log / Kayıt Temizleme ekranından yönetilir (KK-10).
const API_BASE = '/api/auth/login-audit';

// LoginAudit.EventType (§7.6)
const EVENT_TYPES = {
  1: { label: 'Giriş', tone: 'ok' },
  2: { label: 'Çıkış', tone: 'muted' },
  3: { label: 'Başarısız', tone: 'warn' },
  4: { label: 'Kilitlendi', tone: 'danger' },
  5: { label: 'Token yenileme', tone: 'muted' },
  6: { label: 'Şifre değişimi', tone: 'info' },
};

const PLATFORMS = { 1: 'Web', 2: 'Android', 3: 'iOS', 4: 'Servis' };

// ResultCode'lar loga yazılan AYRINTILI sebeplerdir; kullanıcıya dönen mesaj her zaman tekildir
// (G-L-02). Bu ekran denetim ekranı olduğu için gerçek sebebi gösterir.
const RESULT_CODES = {
  INVALID_PASSWORD: 'Şifre hatalı',
  USER_NOT_FOUND: 'Kullanıcı bulunamadı',
  LOCKED: 'Hesap kilitli',
  INACTIVE: 'Hesap pasif',
  CANCELLED: 'Hesap iptal',
  LICENSE_LIMIT: 'Lisans sınırı',
  LOGIN_WINDOW_CLOSED: 'Giriş saati dışında',
  LOCAL_LOGIN_DISABLED: 'Yerel giriş kapalı',
  PASSWORD_EXPIRED: 'Şifre süresi doldu',
  SINGLE_SESSION: 'Tek oturum kuralı',
  CLIENT_VERSION_TOO_OLD: 'Eski istemci sürümü',
};

const COLUMN_FILTER_META = {
  loginName: { type: 'text', odataField: 'LoginName' },
  userFullName: { type: 'text', odataField: 'UserFullName' },
  representativeCode: { type: 'text', odataField: 'RepresentativeCode' },
  eventType: {
    type: 'select', odataField: 'EventType',
    options: Object.entries(EVENT_TYPES).map(([v, o]) => ({ value: v, label: o.label })),
  },
  platform: {
    type: 'select', odataField: 'Platform',
    options: Object.entries(PLATFORMS).map(([v, l]) => ({ value: v, label: l })),
  },
  resultCode: { type: 'text', odataField: 'ResultCode' },
  ipAddress: { type: 'text', odataField: 'IpAddress' },
  clientVersion: { type: 'text', odataField: 'ClientVersion' },
  timestamp: { type: 'date', odataField: 'Timestamp' },
};
const SEARCH_FIELDS = ['LoginName', 'UserFullName', 'RepresentativeCode', 'IpAddress'];

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');

export function LoginAuditPage() {
  usePageMeta('Giriş Günlüğü', 'Güvenlik & İzleme');

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Timestamp',
    defaultSortDir: 'desc',   // günlükte en yeni kayıt en üstte olmalı
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('timestamp', {
      header: 'Zaman', meta: { filterType: 'date' }, cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.accessor('eventType', {
      header: 'Olay', meta: { filterType: 'select' },
      cell: (i) => {
        const e = EVENT_TYPES[i.getValue()] || { label: '-', tone: 'muted' };
        return <span className={`sec-badge sec-badge--${e.tone}`}>{e.label}</span>;
      },
    }),
    columnHelper.accessor('loginName', { header: 'Giriş adı', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('userFullName', { header: 'Kullanıcı', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('representativeCode', {
      header: 'Temsilci', meta: { filterType: 'text' },
      cell: (i) => { const v = i.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('resultCode', {
      header: 'Sonuç', meta: { filterType: 'text' },
      cell: (i) => { const v = i.getValue(); return v ? (RESULT_CODES[v] || v) : '-'; },
    }),
    columnHelper.accessor('platform', {
      header: 'Platform', meta: { filterType: 'select' }, cell: (i) => PLATFORMS[i.getValue()] || '-',
    }),
    columnHelper.accessor('clientVersion', { header: 'Sürüm', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('ipAddress', { header: 'IP', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('isSupportUser', {
      header: 'Destek', enableColumnFilter: false,
      // KK-15: destek kullanıcısının tüm oturumları etiketlenir — çoklu kullanımın izlenebilirliği buna bağlı.
      cell: (i) => (i.getValue() ? <span className="sec-badge sec-badge--info">DESTEK</span> : '-'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Timestamp', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Zaman', valueGetter: ({ row }) => fmtDateTime(row.timestamp) },
        { headerName: 'Olay', valueGetter: ({ row }) => EVENT_TYPES[row.eventType]?.label || '' },
        { field: 'loginName', headerName: 'Giriş adı' },
        { field: 'userFullName', headerName: 'Kullanıcı' },
        { field: 'representativeCode', headerName: 'Temsilci' },
        { headerName: 'Sonuç', valueGetter: ({ row }) => RESULT_CODES[row.resultCode] || row.resultCode || '' },
        { headerName: 'Platform', valueGetter: ({ row }) => PLATFORMS[row.platform] || '' },
        { field: 'clientVersion', headerName: 'Sürüm' },
        { field: 'ipAddress', headerName: 'IP' },
        { field: 'deviceId', headerName: 'Cihaz anahtarı' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `giris-gunlugu_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kullanıcı, giriş adı, temsilci veya IP ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
        ><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Giriş Günlüğü"
        subtitle="Web, Android ve iOS dahil tüm giriş/çıkış denemeleri. Salt okunur — saklama süresi Zamanlanmış Görevler › Log / Kayıt Temizleme ekranından yönetilir."
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={rows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          emptyText="Giriş kaydı bulunamadı"
        />
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}

export default LoginAuditPage;
